import { useState, useRef, useEffect, useMemo, type MouseEvent as ReactMouseEvent } from 'react';
import { ActionIcon, Box, Flex, Stack, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import type { NavItem } from '../../../features/layout/Navbar/Navbar';
import DisplayIcon from '../../iconSelector/DispalyIcon';
import CustomIcon from '../Icons/CustomIcon';
import styles from './QuickActionsFAB.module.css';

interface QuickActionsFABProps {
  menuItems: NavItem[];
  isLoading?: boolean;
}

const flattenMenuItems = (items: NavItem[]): NavItem[] =>
  items.flatMap(item => [item, ...(item.childMenus ? flattenMenuItems(item.childMenus) : [])]);

const QuickActionsFAB = ({ menuItems, isLoading = false }: QuickActionsFABProps) => {
  const navigate = useNavigate();
  const [modalOpened, setModalOpened] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);
  const [hovered, setHovered] = useState(false);

  const quickCreateItems = useMemo(
    () =>
      flattenMenuItems(menuItems)
        .filter(item =>
          item.isActive === true &&
          item.isQuickCreate === true &&
          Boolean(item.quickCreateName?.trim()) &&
          Boolean(item.navMenuUrl?.trim()),
        )
        .map(item => ({
          key: item.id || item.navMenuUrl,
          label: item.quickCreateName?.trim() || '',
          route: item.navMenuUrl,
          iconName: item.iconName,
          iconColor: item.iconColor || '#475569',
        })),
    [menuItems],
  );

  const handleQuickCreate = (route: string) => {
    setModalOpened(false);
    navigate(route);
  };

  const handleMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (fabRef.current) {
      hasMovedRef.current = false;
      const rect = fabRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const updatePosition = () => {
      setPosition({
        x: Math.max(20, window.innerWidth - 80),
        y: Math.max(20, window.innerHeight - 80),
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      hasMovedRef.current = true;
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const maxX = window.innerWidth - 60;
      const maxY = window.innerHeight - 60;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleClick = () => {
    if (!hasMovedRef.current) {
      setModalOpened(!modalOpened);
    }
  };

  const menuContentHeight = 52 + (quickCreateItems.length * 56);
  const menuHeight = Math.min(550, menuContentHeight);

  const calculateMenuPosition = () => {
    const menuWidth = 300;
    const padding = 20;
    const bottomBuffer = 100;
    const fabSize = 60;
    const gap = 20;

    let left = 0;
    let top = 0;

    const isFabInBottomHalf = position.y + fabSize / 2 > window.innerHeight / 2;

    left = position.x - menuWidth - gap;
    if (left < padding) {
      left = position.x + fabSize + gap;
    }

    if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - menuWidth - padding;
    }

    if (left < padding) {
      left = padding;
    }

    if (isFabInBottomHalf) {
      top = position.y - menuHeight - gap;

      if (top < padding) {
        top = padding;
      }
    } else {
      top = position.y - menuHeight / 2 + fabSize / 2;

      if (top < padding) {
        top = position.y + fabSize + gap;
      }
    }

    const minBottomSpace = padding + bottomBuffer;
    if (top + menuHeight > window.innerHeight - minBottomSpace) {
      const maxTop = window.innerHeight - menuHeight - minBottomSpace;
      top = Math.max(padding, maxTop);
    }

    return { left, top };
  };

  const menuPosition = modalOpened ? calculateMenuPosition() : { left: 0, top: 0 };

  if (isLoading || quickCreateItems.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        ref={fabRef}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: '60px',
          height: '60px',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          transition: isDragging ? 'none' : 'box-shadow 200ms ease',
          zIndex: 1001,
        }}
        onMouseOver={() => setHovered(true)}
        onMouseOut={() => setHovered(false)}
      >
        <ActionIcon
          size="xl"
          radius="50%"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#1976d2',
            color: 'white',
            boxShadow: isDragging
              ? '0 8px 24px rgba(25, 118, 210, 0.5)'
              : '0 4px 12px rgba(25, 118, 210, 0.4)',
            pointerEvents: 'none',
            opacity: (hovered || modalOpened) ? 1 : 0.5,
            transition: 'opacity 500ms ease'
          }}
          className={styles.fabButton}
        >
          {/* <IconCommand size={28} stroke={2} /> */}
          <CustomIcon iconType={modalOpened ? 'chevronDown' : 'up'} h={30} w={30} color='white' hoverColor='white' stroke={2} style={{ display: 'flex' }} />
        </ActionIcon>
      </Box>

      {modalOpened && (
        <>
          <Box
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              zIndex: 999
            }}
            onClick={() => setModalOpened(false)}
          />
          <Box
            style={{
              position: 'fixed',
              left: `${menuPosition.left}px`,
              top: `${menuPosition.top}px`,
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '16px',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
              zIndex: 1000,
              minWidth: '280px',
              maxWidth: '320px',
              maxHeight: `min(${menuHeight}px, calc(100vh - 100px))`,
              overflow: 'auto',
            }}
            className={styles.quickCreateMenu}
          >
            <Text fw={600} c='dimmed' fz={11} mb="md" tt="uppercase">
              QUICK CREATE
            </Text>
            <Stack gap="xs">
              {quickCreateItems.map((item) => (
                  <Box
                    key={item.key}
                    p="sm"
                    style={{
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      backgroundColor: '#f8f9fa',
                    }}
                    onClick={() => handleQuickCreate(item.route)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e7f5ff';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <Flex align="center" gap="sm">
                      <DisplayIcon iconName={item.iconName} iconColor={item.iconColor} width={24} height={24} />
                      <Text fw={600} fz={12} c="black">
                        {item.label}
                      </Text>
                    </Flex>
                  </Box>
                ))}
            </Stack>
          </Box>
        </>
      )}
    </>
  );
};

export default QuickActionsFAB;
